import { useState } from 'react';

import { useDialogContext } from '../../contexts/DialogContext';
import { useAsyncStatus, useOnMount, useToggle } from '../../hooks';
import { get, put } from '../../redux/api';
import { LabeledCheckbox, LabeledTextInput } from '../generic';
import Accordion from '../generic/Accordion';
import { AdminCardBody } from '../generic/AdminCardBody';
import Button from '../generic/Button';
import Card from '../generic/Card/Card';
import Text from '../generic/Text';

type CustomQueueFormProps = {
  customQueue: string;
  onUpdate: (queue: string) => void;
  isUpdating: boolean;
};

const CustomQueueForm = ({ customQueue, onUpdate, isUpdating }: CustomQueueFormProps) => {
  const [newCustomQueue, setNewCustomQueue] = useState(customQueue);
  // I have no idea what problem eslint has with the names below

  const [isCustomQueueEnabled, toggleCustomQueueEnabled] = useToggle(customQueue !== '');
  return (
    <>
      <div className="margin-bottom-md">
        <Text.Paragraph>
          Defines the queue where background jobs will be queued. A custom queue is useful for organizations that submit
          large batches of builds because it prevents downgrading performance of the default queue which is shared by
          almost all organizations.
        </Text.Paragraph>
        <Text.Paragraph>
          Please post a message in <code>#team-system</code> before making any changes.
        </Text.Paragraph>
      </div>
      <div className="margin-bottom-md">
        <LabeledCheckbox
          label="Enable Custom Queue"
          isChecked={isCustomQueueEnabled}
          onChange={toggleCustomQueueEnabled}
          isDisabled={isUpdating}
        />
        <LabeledTextInput
          label="Name"
          value={newCustomQueue}
          onChange={setNewCustomQueue}
          isDisabled={!isCustomQueueEnabled || isUpdating}
        />
      </div>
      <div>
        <Button
          onClick={() => onUpdate(isCustomQueueEnabled ? newCustomQueue : '')}
          isDisabled={isUpdating || (isCustomQueueEnabled && !newCustomQueue)}
        >
          Save
        </Button>
      </div>
    </>
  );
};

const CustomProvidedBuildsQueueCard = () => {
  const [customQueue, setCustomQueue] = useState<string | undefined>(undefined);
  const getCustomQueueStatus = useAsyncStatus(true);
  const updateCustomQueueStatus = useAsyncStatus();
  const { onConfirm } = useDialogContext();

  const getCustomQueue = async () => {
    try {
      const { data } = await get('/api/organization/custom-provided-builds-queue');
      setCustomQueue(data?.name);
      getCustomQueueStatus.onSuccess();
    } catch (e) {
      getCustomQueueStatus.onError(e);
    }
  };

  const updateCustomQueue = async (newCustomQueue: string) => {
    try {
      updateCustomQueueStatus.onPending();
      await put('/api/organization/custom-provided-builds-queue', { name: newCustomQueue });
      setCustomQueue(newCustomQueue);
      updateCustomQueueStatus.onSuccess();
    } catch (e) {
      updateCustomQueueStatus.onError(e);
    }
  };

  const handleCustomQueueUpdate = (newCustomQueue: string) => {
    onConfirm({
      title: 'Update the queue for your organization',
      body: (
        <div>
          Are you sure you want to set <code>{newCustomQueue}</code> as the queue for your organization?
        </div>
      ),
      onConfirm: () => updateCustomQueue(newCustomQueue),
    });
  };

  useOnMount(getCustomQueue);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="custom-provided-builds-queue" isInitiallyOpen={false}>
          <Accordion.Target>Custom Provided Builds Queue</Accordion.Target>
          <Accordion.Body>
            <Text.H4>Custom Provided Builds Queue</Text.H4>
            <AdminCardBody
              useAsyncStatuses={[getCustomQueueStatus]}
              initialUseAsyncStatuses={[getCustomQueueStatus]}
          >
              <CustomQueueForm
                customQueue={customQueue || ''}
                onUpdate={handleCustomQueueUpdate}
                isUpdating={updateCustomQueueStatus.status.loading}
            />
            </AdminCardBody>
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default CustomProvidedBuildsQueueCard;
